import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Skeleton, Flex, Text, Image, Box, useDisclosure } from '@chakra-ui/react'

import Offering from 'components/offering/plan/Offering'
import IBalert from 'components/common/IBalert/IBalert'
import { useOfferingProvider } from 'providers/offeringProvider/OfferingProvider'
import LaptopPlan from 'assets/images/laptop-plan.svg'
import Laptop2Plan from 'assets/images/laptop2-plan.svg'
import PagosPerson from 'assets/images/pagos-person.svg'
import DonePlan from 'assets/images/done-plan.svg'
import Lego from 'assets/images/lego.svg'
import CellphonePlan from 'assets/images/cellphone-plan.svg'
import { IFetchedOfferingsSelected, IOfferingsB, IOfferingsSelected, IResponseGeneric, ISaveOffering } from 'services/IApi.d'
import FooterButtons from 'components/common/FooterButtons/FooterButtons'
import { getOffering, saveOfferings, updateOfferings } from 'services/api'
import { useGlobalContext } from 'providers/GlobalProvider'
import { isSuccessStatus } from 'utils/functions/validations'

const PlansPage = () => {
  const navigate = useNavigate()
  const { isOpen, onOpen, onClose } = useDisclosure()
  const [selectedPlan, setSelectedPlan] = useState<IOfferingsB>(null)
  const [loadingNext, setLoadingNext] = useState<boolean>(false)
  const [oldPlan, setOldPlan] = useState<string>(null)
  const { loading, offerings, selectedOfferings, setSelectedOfferings, changeLoadingPlan, setChangePlan, sendDataLayer } = useOfferingProvider()
  const { idEmpresa, idSuscriptor } = useGlobalContext()

  const selectPlan = (plan: IOfferingsB) => setSelectedPlan(plan)

  const primaryText = 'Continuar'
  const secondaryText = 'Volver'
  const title = 'Compará nuestros planes'
  const subtitle = 'Podés operar con todos tus bancos y cuentas, además de usuarios ilimitados.'

  const primaryAction = async () => {
    setLoadingNext(true)
    const dataSelectedPlan: ISaveOffering[] = [{
      codeDn: selectedPlan?.codeDn,
      subscriberId: idSuscriptor,
      companyId: idEmpresa,
      additionalService: false,
    }]
    setSelectedOfferings(older => ({ ...older, plan: structuredClone(selectedPlan) }))
    if (selectedPlan?.codeDn === oldPlan) {
      setChangePlan(false)
      navigate('/cuentas')
      return false
    }
    if (selectedPlan?.codeDn !== oldPlan && oldPlan !== undefined) {
      setChangePlan(true)
      const response: IResponseGeneric = await updateOfferings(dataSelectedPlan)
      if (isSuccessStatus(response?.status)) {
        setSelectedOfferings(old => ({ ...old, additionalServices: [] }))
        navigate('/cuentas')
        return false
      }
    }
    const response: IResponseGeneric = await saveOfferings(dataSelectedPlan)
    if (isSuccessStatus(response?.status)) {
      sendDataLayer({
        event: 'completed_onboarding_step',
        onboarding_step: 'Seleccionar plan',
        plan: dataSelectedPlan[0].codeDn,
        first_time: oldPlan ? 'No' : 'Si'
      })
      navigate('/cuentas')
      return false
    }

    onOpen()
    return true
  }

  const secondaryAction = () => navigate('/dashboard')

  const getSelectedOfferings = async (): Promise<IOfferingsSelected> => {
    try {
      const response: IFetchedOfferingsSelected = await getOffering(idSuscriptor, idEmpresa)
      const selected = response.data.filter(o => !o.additionalService)[0]
      return selected
    } catch (e) {
      return null
    }
  }

  useEffect(() => {
    if (selectedOfferings?.plan) {
      setSelectedPlan(offerings?.filter(of => of.codeDn === selectedOfferings.plan.codeDn)[0])
      setOldPlan(offerings?.filter(of => of.codeDn === selectedOfferings.plan.codeDn)[0]?.codeDn)
      changeLoadingPlan(false)
    } else if (offerings) {
      getSelectedOfferings()
        .then((selected: IOfferingsSelected) => {
          setSelectedPlan(offerings?.filter(of => of?.codeDn === selected?.codeDn)[0])
          setOldPlan(offerings?.filter(of => of?.codeDn === selected?.codeDn)[0]?.codeDn)
          changeLoadingPlan(false)
        })
    }
  }, [offerings])

  return (
    <Flex direction='column'>
      <Box
        position='absolute'
        background='linear-gradient(#FFFFFF, 60%, #EBDDFF, 90%, #FFFFFF)'
        left={0}
        top={0}
        height='100%'
        width='100%'
        zIndex={-1000}
      />
      <Text textStyle='h1' data-testid='title'>{title}</Text>
      <Text textStyle='bodyLg' data-testid='subtitle'>{subtitle}</Text>
      <Flex direction='row' justifyContent='space-between' gap={3} data-testid='container' my={8}>
        {(offerings && !loading.current.plan) ?
          offerings.map(offer =>
            <Offering
              key={offer.codeDn}
              title={offer.denomination}
              price={offer.price}
              limits={{
                transferAmountLimitArs: offer.transferAmountLimitArs,
                transferAmountLimitUsd: offer.transferAmountLimitUsd,
                transferQuantityLimit: offer.transferQuantityLimit,
                consultationLimit: offer.consultationLimit,
              }}
              codeDN={offer.codeDn}
              type={offer.denomination === 'PremiumVIP' ? 2 : 1}
              selectedPlan={selectedPlan?.codeDn}
              action={selectPlan}
            />
          )
          :
          <>
            <Skeleton width='100%' height={800} />
            <Skeleton width='100%' height={800} />
            <Skeleton width='100%' height={800} />
            <Skeleton width='100%' height={800} />
            <Skeleton width='100%' height={800} />
          </>
        }
      </Flex>
      <Flex direction='column' data-testid='info'>
        <Text textStyle='h2Bold' mb={12}>Todos los planes te permitirán</Text>
        <Flex direction='row'>
          <Flex direction='column' flex={0.4} pr={32} pt={16}>
            <Text textStyle='h4' mb={4}>Hacer todo tipo de transferencias desde un solo lugar</Text>
            <Text textStyle='bodyLg' color='neutral.20' mb={16}>Operá con todos tus bancos desde una misma plataforma y realizá todo tipo de transferencias.</Text>
            <Text textStyle='bodyMdSemi' color='tertiary.500'>Nuestros planes tienen distintos montos y cantidad de transferencias. ¡Elegí el que más te conviene!</Text>
          </Flex>
          <Flex direction='column' flex={0.6}>
            <Image alt='laptop image' src={LaptopPlan} my={4} />
          </Flex>
        </Flex>
        <Flex
          direction='row'
          borderStyle='solid'
          borderWidth='1px'
          borderColor='secondary.80'
          borderRadius={12}
          backgroundColor='white'
          px={16}
        >
          <Flex direction='column' flex={0.6} justifyContent='center'>
            <Image alt='Realizar pagos' src={PagosPerson} my={8} boxSize='70%' />
          </Flex>
          <Flex direction='column' my={16} flex={0.4} justifyContent='center'>
            <Text textStyle='h4' mb={4}>Realizar todos los pagos<br />de tu empresa</Text>
            <Text textStyle='bodyLg' mb='8'>Pagá impuestos, servicios o AFIP. También hacé<br />pagos entre empresas, conocidos como BtoB.</Text>
            <Flex direction='row'>
              <Image alt='check image' src={DonePlan} ml={2} />
              <Text textStyle='bodyMdSemi' ml={2}>100% digital</Text>
            </Flex>
            <Flex direction='row'>
              <Image alt='check image' src={DonePlan} ml={2} />
              <Text textStyle='bodyMdSemi' ml={2}>Múltiples facturas</Text>
            </Flex>
            <Flex direction='row'>
              <Image alt='check image' src={DonePlan} ml={2} />
              <Text textStyle='bodyMdSemi' ml={2}>Todo en un solo lugar</Text>
            </Flex>
          </Flex>
        </Flex>
        <Flex direction='row'>
          <Flex direction='column' flex={0.4} pr={32} pt={16}>
            <Text textStyle='h4' mb={4}>Descargar reportes de todas <br />tus operaciones</Text>
            <Text textStyle='bodyLg' color='neutral.20' mb={16}>Accedé a información de tus transferencias, pagos,<br />saldos y movimientos, ¡en el formato que prefieras!</Text>
            <Text textStyle='bodyMdSemi' color='tertiary.500'>Nuestros planes tienen distinta cantidad de descargas<br />mensuales. ¡Elegí el que más te conviene!</Text>
          </Flex>
          <Flex direction='column' flex={0.6}>
            <Image alt='laptop image' src={Laptop2Plan} my={4} />
          </Flex>
        </Flex>
        <Flex
          direction='row'
          borderStyle='solid'
          borderWidth='1px'
          borderColor='secondary.80'
          borderRadius={12}
          backgroundColor='white'
          px={16}
        >
          <Flex direction='column' justifyContent='center'>
            <Image alt='lego image' src={Lego} my={8} w='85px' h='105px' />
          </Flex>
          <Flex direction='column' mx={8} my={16} justifyContent='center'>
            <Text textStyle='h5Bold' mb={4}>Integrar tus herramientas de gestión con Interbanking a través de InterAPIs</Text>
            <Text textStyle='bodyLg'>Conectá tus software como SAP, Tango, Excel o Power BI ¡con cualquiera de nuestros planes abonados!</Text>
          </Flex>
        </Flex>
        <Flex direction='row' my={16}>
          <Text textStyle='h4'>Aprovechá Interbanking al máximo</Text>
        </Flex>
        <Flex direction='row' mb={96}>
          <Flex
            direction='column'
            p={16}
            borderStyle='solid'
            borderWidth='1px'
            borderColor='tertiary.60'
            borderRadius={12}
            backgroundColor='tertiary.100'
            justifyContent='center'
            height={72}
            flex={0.35}
          >
            <Text textStyle='h5Bold'>Una plataforma, múltiples beneficios</Text>
            <Text>Pagá servicios, enviá transferencias, depositá Echeqs<br />e invertí en Fondos Comunes de Inversión.<br />También gestioná facturas electrónicas y<br />depósitos judiciales.  ¡Todo desde una plataforma digital, simple y segura!</Text>
          </Flex>
          <Flex direction='column' flex={0.65}>
            <Flex direction='column' ml={96}>
              <Text textStyle='h5Bold'>Agilizá tus operaciones<br />desde nuestra App</Text>
              <Text textStyle='bodyLg'>Autorizá y enviá transferencias, pagá tus<br />servicios, consultá tus saldos y aprobá<br />operaciones desde cualquier lugar.</Text>
            </Flex>
            <Flex>
              <Image alt='cellphone image' src={CellphonePlan} position='absolute' bottom={14} />
            </Flex>
          </Flex>
        </Flex>
      </Flex>
      <FooterButtons
        onBack={secondaryAction}
        onNext={primaryAction}
        backDisabled={false}
        nextDisabled={!selectedPlan}
        loading={loadingNext}
        backText={secondaryText}
        nextText={primaryText}
      />
      <IBalert
        isOpen={isOpen}
        onClose={onClose}
        title='No podemos guardar tu selección'
        text='Estamos trabajando para solucionarlo, volvé a intentar más tarde.'
        buttonClose
        type='error'
        variant='sm'
      />
    </Flex>
  )
}

export default PlansPage
